import { getStoredPlacePhotoUrl } from "@/utils/googlePlaces";

/** What the entity's Google Places record is, wherever this response carries it. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const googlePlacesOf = (entity: any) => entity?.google_places || entity?.enrichment_data?.google_places;

// Format price level for display
export function formatPriceLevel(
  priceLevel: string | number | null | undefined,
): { level: number; display: "free" | "paid" } | undefined {
  if (priceLevel === null || priceLevel === undefined) return undefined;

  let level = 0;

  if (typeof priceLevel === "number") {
    if (Number.isNaN(priceLevel) || priceLevel < 0) return undefined;
    if (priceLevel === 0) return { level: 0, display: "free" };
    level = Math.min(priceLevel, 4);
  } else {
    const trimmed = priceLevel.trim();
    if (!trimmed.length) return undefined;

    const priceLevelMap: Record<string, number> = {
      PRICE_LEVEL_FREE: 0,
      PRICE_LEVEL_INEXPENSIVE: 1,
      PRICE_LEVEL_MODERATE: 2,
      PRICE_LEVEL_EXPENSIVE: 3,
      PRICE_LEVEL_VERY_EXPENSIVE: 4,
    };

    if (priceLevelMap[trimmed] !== undefined) {
      level = priceLevelMap[trimmed];
      if (level === 0) return { level: 0, display: "free" };
    } else {
      const numeric = Number(trimmed);
      if (!Number.isNaN(numeric)) {
        if (numeric === 0) return { level: 0, display: "free" };
        if (numeric < 0) return undefined;
        level = Math.min(Math.max(1, numeric), 4);
      } else if (/^\$+$/.test(trimmed)) {
        level = Math.min(trimmed.length, 4);
      } else {
        return undefined;
      }
    }
  }

  return { level, display: "paid" };
}

// Format field names for display (e.g., "fast_food_restaurant" -> "Fast Food Restaurant")
export function formatFieldName(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
    .trim();
}

// Normalize weekday descriptions from various formats
function normaliseWeekdayDescriptions(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(entry => (typeof entry === "string" ? entry.trim() : "")).filter(Boolean);
  }
  if (typeof value === "object" && "weekday_text" in (value as Record<string, unknown>)) {
    const weekdayText = (value as { weekday_text?: unknown }).weekday_text;
    if (Array.isArray(weekdayText)) {
      return normaliseWeekdayDescriptions(weekdayText);
    }
  }
  return [];
}

// Extract opening hours data from entity
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractOpeningHours(entity: any): { lines: string[]; openNowText?: string } | undefined {
  // EntitySummary has opening_hours as a top-level string[]
  if (Array.isArray(entity?.opening_hours) && entity.opening_hours.length > 0) {
    return { lines: entity.opening_hours };
  }

  const gp = googlePlacesOf(entity);
  if (!gp) return undefined;

  const openingHours = gp.opening_hours as Record<string, unknown> | undefined;
  if (!openingHours) return undefined;

  const current =
    typeof openingHours.current === "object" ? (openingHours.current as Record<string, unknown>) : undefined;
  const regular =
    typeof openingHours.regular === "object" ? (openingHours.regular as Record<string, unknown>) : undefined;

  const lines = normaliseWeekdayDescriptions(current?.weekday_descriptions);
  const fallbackLines = lines.length ? lines : normaliseWeekdayDescriptions(regular?.weekday_descriptions);
  const legacyLines = fallbackLines.length ? fallbackLines : normaliseWeekdayDescriptions(openingHours);

  let openNowText: string | undefined;
  const openNowCandidate = current?.open_now ?? openingHours.open_now;
  if (typeof openNowCandidate === "boolean") {
    openNowText = openNowCandidate ? "Open now" : "Closed now";
  } else if (typeof openNowCandidate === "string" && openNowCandidate.trim().length) {
    openNowText = openNowCandidate;
  }

  if (legacyLines.length === 0 && !openNowText) return undefined;

  return {
    lines: legacyLines,
    openNowText,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function entityRating(entity: any): number | undefined {
  const rating = googlePlacesOf(entity)?.rating ?? entity?.rating;
  if (rating === undefined || rating === null) return undefined;
  if (typeof rating === "number") return Number.isFinite(rating) ? rating : undefined;
  const cleaned = String(rating).replace(/,/g, "").trim();
  if (!cleaned) return undefined;
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function entityReviewCount(entity: any): number | undefined {
  const count = googlePlacesOf(entity)?.user_ratings_total ?? entity?.rating_count;
  if (count === undefined || count === null) return undefined;
  if (typeof count === "number") return Number.isFinite(count) ? count : undefined;
  const cleaned = String(count).replace(/,/g, "").trim();
  if (!cleaned) return undefined;
  const parsed = Number.parseInt(cleaned, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function entityPrimaryType(entity: any): string | undefined {
  const gp = googlePlacesOf(entity);
  return gp?.primary_type || entity?.category || (Array.isArray(gp?.types) ? gp.types[0] : undefined);
}

/** Every photo the entity already holds, best first, then any loaded on request. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function storedPlacePhotoUrls(entity: any): string[] {
  const photos = googlePlacesOf(entity)?.photos;
  if (!Array.isArray(photos)) return [];
  return photos.map(photo => getStoredPlacePhotoUrl(photo)).filter((url): url is string => Boolean(url));
}
